import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import {
  Clock, User, Car, DollarSign,
  Phone, Mail, MapPin, Award,
  Gauge, Navigation, AlertTriangle, CheckCircle, XCircle,
  Activity, ChevronDown, ChevronUp, ShieldCheck, ShieldAlert, ShieldX,
  Lock, Unlock, CreditCard, Smartphone, Banknote, FileText,
} from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import { useDriver } from '../hooks/useDriver';
import { useDriverTrips } from '../hooks/useDriverTrips';
import { useDriverLocation } from '../hooks/useDriverLocation';
import { useDrivingSessions } from '../hooks/useDrivingSessions';
import { useUtloggadToday } from '../hooks/useUtloggadToday';
import { useComplianceVerdict } from '../hooks/useComplianceVerdict';
import { useComplianceEvents } from '../hooks/useComplianceEvents';
import { useRestPeriods } from '../hooks/useRestPeriods';
import { useStintHistory, type StintRow } from '../hooks/useStintHistory';
import { useTransactions } from '../hooks/useTransactions';
import { useIncomeByApp } from '../hooks/useIncomeByApp';
import { useDriverApps } from '../hooks/useDriverApps';
import type { Trip, DrivingSession, ComplianceStatus } from '../lib/types';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const MAP_FALLBACK: [number, number] = [59.3293, 18.0686];

const TRIP_STATUS_COLORS: Record<Trip['status'], string> = {
  pending:   'bg-slate-100 text-slate-600',
  active:    'bg-blue-50 text-blue-600',
  completed: 'bg-emerald-50 text-emerald-600',
  cancelled: 'bg-red-50 text-red-600',
};

// ── Utility helpers ──────────────────────────────────────────────────────────

const formatDuration = (minutes: number) =>
  minutes <= 0 ? '0 min'
  : minutes < 60 ? `${minutes} min`
  : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

const breakDurationMin = (b: { started_at: string; ended_at: string | null }) => {
  if (!b.ended_at) return 0;
  return Math.round((new Date(b.ended_at).getTime() - new Date(b.started_at).getTime()) / 60000);
};

// Local calendar day (local midnight) — MUST match the mobile app's
// startOfDay() so night-shift sessions land on the same day on both surfaces.
const pad2 = (n: number) => String(n).padStart(2, '0');
const localDayKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// ── Stint helpers — login/logout pairs + utloggning windows, same math as mobile ──

interface StintPair { start: string; end: string | null; minutes: number }
interface LogoutGap { start: string; end: string; minutes: number }

/** Login→logout work stints whose login falls on the given local day. */
const stintPairsForDay = (stints: StintRow[], dayKey: string): StintPair[] => {
  const pairs: StintPair[] = [];
  let open: string | null = null;
  for (const s of stints) {
    if (s.event === 'login') {
      if (open == null) open = s.occurred_at;
    } else if (open != null) {
      if (localDayKey(new Date(open)) === dayKey) {
        pairs.push({
          start: open,
          end: s.occurred_at,
          minutes: Math.max(0, Math.round((new Date(s.occurred_at).getTime() - new Date(open).getTime()) / 60000)),
        });
      }
      open = null;
    }
  }
  if (open != null && localDayKey(new Date(open)) === dayKey) {
    pairs.push({
      start: open,
      end: null,
      minutes: Math.max(0, Math.round((Date.now() - new Date(open).getTime()) / 60000)),
    });
  }
  return pairs;
};

/** Closed logout→login windows (≥1 min) starting on the given local day. */
const logoutGapsForDay = (stints: StintRow[], dayKey: string): LogoutGap[] => {
  const gaps: LogoutGap[] = [];
  let lastLogout: string | null = null;
  for (const s of stints) {
    if (s.event === 'logout') {
      lastLogout = s.occurred_at;
    } else {
      if (lastLogout != null && localDayKey(new Date(lastLogout)) === dayKey) {
        const minutes = Math.round((new Date(s.occurred_at).getTime() - new Date(lastLogout).getTime()) / 60000);
        if (minutes >= 1) gaps.push({ start: lastLogout, end: s.occurred_at, minutes });
      }
      lastLogout = null;
    }
  }
  return gaps;
};

/** Total utloggning minutes from closed gaps starting at/after fromMs. */
const utloggadMinInRange = (stints: StintRow[], fromMs: number): number => {
  let total = 0;
  let lastLogout: number | null = null;
  for (const s of stints) {
    const t = new Date(s.occurred_at).getTime();
    if (s.event === 'logout') {
      lastLogout = t;
    } else {
      if (lastLogout != null && lastLogout >= fromMs) {
        const minutes = Math.round((t - lastLogout) / 60000);
        if (minutes >= 1) total += minutes;
      }
      lastLogout = null;
    }
  }
  return total;
};

/** On-duty stint minutes inside a local calendar day [dayStartMs, dayEndMs) — each
 *  login→logout span clamped to the day, so an overnight shift splits across days
 *  (same allocation as the mobile getDayReport / computeStintMinutes). */
const stintOnDutyMinutesForDay = (stints: StintRow[], dayStartMs: number, dayEndMs: number): number => {
  const cap = Math.min(Date.now(), dayEndMs);
  let totalMs = 0;
  let openStart: number | null = null;
  for (const s of stints) {
    if (s.event === 'login') {
      if (openStart == null) openStart = new Date(s.occurred_at).getTime();
    } else if (openStart != null) {
      const t = new Date(s.occurred_at).getTime();
      totalMs += Math.max(0, Math.min(t, cap) - Math.max(openStart, dayStartMs));
      openStart = null;
    }
  }
  if (openStart != null) totalMs += Math.max(0, cap - Math.max(openStart, dayStartMs));
  return Math.round(totalMs / 60000);
};

const getComplianceStatus = (todayMinutes: number, maxSingleBreakMin: number): ComplianceStatus => {
  if (todayMinutes > 780) return 'violation';                              // >13h Vägarbetstidslagen
  if (todayMinutes > 540 && maxSingleBreakMin < 45) return 'warning';     // >9h, no 45-min break
  if (todayMinutes > 360 && maxSingleBreakMin < 30) return 'warning';     // >6h, no 30-min break
  return 'ok';
};

const getSessionStatus = (session: DrivingSession): ComplianceStatus => {
  const maxBreak = (session.breaks ?? []).reduce((m, b) => Math.max(m, breakDurationMin(b)), 0);
  if (session.driving_minutes > 780) return 'violation';
  if (session.driving_minutes > 540 && maxBreak < 45) return 'warning';
  if (session.driving_minutes > 360 && maxBreak < 30) return 'warning';
  return 'ok';
};

const getDailyMinutes = (sessions: DrivingSession[], daysBack: number): { labels: string[]; data: number[] } => {
  const DAY_NAMES = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
  const labels: string[] = [];
  const data: number[] = [];
  for (let i = daysBack - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = localDayKey(d);
    labels.push(DAY_NAMES[d.getDay()]);
    data.push(
      sessions
        .filter(s => localDayKey(new Date(s.started_at)) === dateStr)
        .reduce((sum, s) => sum + (s.driving_minutes ?? 0), 0) / 60
    );
  }
  return { labels, data };
};

// ── Sub-components ───────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className ?? ''}`} />;
}

function MetricCard({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl border border-slate-100 bg-white hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-electric-50 text-electric-500 shrink-0">
          {icon}
        </div>
        <span className="text-sm text-slate-600">{label}</span>
      </div>
      <div className="text-sm font-semibold text-slate-800">{children}</div>
    </div>
  );
}

const COMPLIANCE_CONFIG: Record<ComplianceStatus, {
  label: string; bg: string; text: string; border: string; Icon: React.ComponentType<{ className?: string }>;
}> = {
  ok:        { label: 'Godkänd',    bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', Icon: ShieldCheck },
  warning:   { label: 'Varning',    bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   Icon: ShieldAlert },
  violation: { label: 'Regelbrott', bg: 'bg-red-50',     text: 'text-red-700',     border: 'border-red-200',     Icon: ShieldX },
};

function CompliancePill({ status, large }: { status: ComplianceStatus; large?: boolean }) {
  const c = COMPLIANCE_CONFIG[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-lg border font-semibold ${c.bg} ${c.text} ${c.border} ${large ? 'px-4 py-2 text-sm' : 'px-2.5 py-1 text-xs'}`}>
      <c.Icon className={large ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
      {c.label}
    </span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const TachographDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('vilotid');
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'all'>('all');

  const { driver, loading: driverLoading, error: driverError } = useDriver(id);
  const { trips, loading: tripsLoading } = useDriverTrips(id);
  const { location } = useDriverLocation(id);
  const { sessions, loading: sessionsLoading } = useDrivingSessions(id);
  const { utloggadMin, utloggadCount } = useUtloggadToday(id);
  const { verdict } = useComplianceVerdict(id);
  const { events: complianceEvents } = useComplianceEvents(id);
  const { rests: restPeriods } = useRestPeriods(id);
  const { stints } = useStintHistory(id);
  const { transactions, loading: txLoading } = useTransactions(id);
  const { rows: incomeByApp, loading: incomeByAppLoading } = useIncomeByApp(id);
  const { apps: driverApps, loading: appsLoading } = useDriverApps(id);

  // Map
  const mapCenter: [number, number] = location ? [location.lat, location.lng] : MAP_FALLBACK;
  const recentTrip = trips.find(t => t.origin_lat != null && t.dest_lat != null);
  const routePoints: [number, number][] = recentTrip
    ? [[recentTrip.origin_lat!, recentTrip.origin_lng!], [recentTrip.dest_lat!, recentTrip.dest_lng!]]
    : [];

  // Income
  const completedTrips = trips.filter(t => t.status === 'completed');
  const cancelledTrips = trips.filter(t => t.status === 'cancelled');
  const totalIncome = completedTrips.reduce((sum, t) => sum + (t.fare ?? 0), 0);
  const avgPerTrip = completedTrips.length > 0 ? totalIncome / completedTrips.length : 0;

  // Compliance — today
  const today = localDayKey(new Date());
  const todaySessions = sessions.filter(s => localDayKey(new Date(s.started_at)) === today);
  const todayBreaks = todaySessions.flatMap(s => s.breaks ?? []);
  const todayBreakMinutes = todayBreaks.reduce((sum, b) => sum + breakDurationMin(b), 0);
  const maxSingleBreak = todayBreaks.reduce((m, b) => Math.max(m, breakDurationMin(b)), 0);
  // Arbetstid today = on-duty stint time clamped to this calendar day (splits overnight
  // shifts across days) minus today's rast — same allocation as the mobile day report.
  const todayStartMs = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
  const todayMinutes = Math.max(0, stintOnDutyMinutesForDay(stints, todayStartMs, todayStartMs + 86400000) - todayBreakMinutes);
  const complianceStatus = getComplianceStatus(todayMinutes, maxSingleBreak);

  // Compliance — week
  const weekSessions = sessions.filter(s => {
    const d = new Date(s.started_at);
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    return d >= weekAgo;
  });
  const weekMinutes = weekSessions.reduce((sum, s) => sum + (s.driving_minutes ?? 0), 0);

  // Compliance — month
  const monthSessions = sessions.filter(s => {
    const d = new Date(s.started_at);
    return d.getMonth() === new Date().getMonth() && d.getFullYear() === new Date().getFullYear();
  });
  const monthMinutes = monthSessions.reduce((sum, s) => sum + (s.driving_minutes ?? 0), 0);
  const monthBreakMinutes = monthSessions
    .flatMap(s => s.breaks ?? [])
    .reduce((sum, b) => sum + breakDurationMin(b), 0);

  // Chart data
  const { labels: chartLabels, data: chartData } = getDailyMinutes(sessions, 7);
  const weekBreakMinutes = weekSessions.flatMap(s => s.breaks ?? []).reduce((sum, b) => sum + breakDurationMin(b), 0);
  const weekMaxBreak = weekSessions.flatMap(s => s.breaks ?? []).reduce((m, b) => Math.max(m, breakDurationMin(b)), 0);

  // Utloggad time per period — same stint-gap math as the mobile report
  const weekUtloggadMin = utloggadMinInRange(stints, Date.now() - 7 * 24 * 60 * 60 * 1000);
  const monthStartMs = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
  const monthUtloggadMin = utloggadMinInRange(stints, monthStartMs);

  // Med kund / ledig today — trips inside today's arbetstid, mirroring mobile
  const todayTripMin = trips.reduce((sum, t) => {
    if (!t.started_at || localDayKey(new Date(t.started_at)) !== today) return sum;
    const endMs = t.ended_at ? new Date(t.ended_at).getTime() : Date.now();
    return sum + Math.max(0, Math.round((endMs - new Date(t.started_at).getTime()) / 60000));
  }, 0);
  const medKundMin = Math.min(todayTripMin, todayMinutes);
  const ledigMin = Math.max(0, todayMinutes - medKundMin);

  // Period filter for the Körpass table
  const todayMidnightMs = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const periodCutoffMs =
    period === 'today' ? todayMidnightMs
    : period === 'week' ? Date.now() - 7 * 24 * 60 * 60 * 1000
    : period === 'month' ? Date.now() - 30 * 24 * 60 * 60 * 1000
    : 0;
  const visibleSessions = sessions.filter(s => new Date(s.started_at).getTime() >= periodCutoffMs);

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#10b981';
    if (score >= 75) return '#06B6D4';
    if (score >= 60) return '#F59E0B';
    return '#ef4444';
  };

  const tabs = [
    { id: 'vilotid', label: 'Vilotidsrapport', icon: <Clock className="w-4 h-4" /> },
    { id: 'korpass', label: 'Körpass',         icon: <Activity className="w-4 h-4" /> },
    { id: 'car',     label: 'Fordon',          icon: <Car className="w-4 h-4" /> },
    { id: 'income',  label: 'Inkomst',         icon: <DollarSign className="w-4 h-4" /> },
  ];

  if (driverError) {
    return (
      <Layout title="Tachograph" breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Tachograph', path: '/tachograph' }, { label: 'Hittades ej' }]}>
        <div className="card flex flex-col items-center justify-center py-24 text-center">
          <XCircle className="w-12 h-12 text-red-300 mb-4" />
          <h3 className="font-semibold text-slate-700 mb-1">Föraren hittades inte</h3>
          <p className="text-sm text-slate-400">{driverError}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Tachograph"
      breadcrumbs={[
        { label: 'Home', path: '/dashboard' },
        { label: 'Tachograph', path: '/tachograph' },
        { label: driverLoading ? '...' : (driver?.full_name ?? 'Förare') },
      ]}
    >
      <div className="card overflow-hidden">

        {/* ── Header banner ── */}
        <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3"
          style={{ background: 'linear-gradient(135deg, #0B1120, #0f2044)' }}>
          <div className="flex flex-wrap items-center gap-3">
            {driverLoading ? (
              <Skeleton className="h-7 w-40 bg-white/10" />
            ) : (
              <>
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                  {driver?.full_name?.toUpperCase() ?? '—'}
                </h2>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                  driver?.status === 'active'
                    ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                    : driver?.status === 'suspended'
                    ? 'bg-red-500/20 border-red-500/30 text-red-400'
                    : 'bg-slate-500/20 border-slate-500/30 text-slate-400'
                }`}>
                  {location ? <span className="live-dot" style={{ width: 6, height: 6 }} /> : null}
                  {driver?.status ?? '—'}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-white/70 text-xs">
                  <User className="w-3 h-3" /> Spåra förare
                </span>
              </>
            )}
            <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white/80 text-xs transition">
              Exportera rapport
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="border-b border-slate-100 flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors relative ${
                activeTab === tab.id ? 'text-electric-600' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.icon}{tab.label}
              {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-500 rounded-t" />}
            </button>
          ))}
        </div>

        {/* ── Date filter (drives the Körpass table) ── */}
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex justify-end">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as 'today' | 'week' | 'month' | 'all')}
            aria-label="Filtrera körpass efter period"
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white cursor-pointer focus:outline-none focus:border-electric-400"
          >
            <option value="all">All tid</option>
            <option value="today">Idag</option>
            <option value="week">Senaste 7 dagarna</option>
            <option value="month">Senaste 30 dagarna</option>
          </select>
        </div>

        {/* ── Content ── */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ── Left: driver info + map ── */}
            <div className="lg:col-span-1 space-y-5">
              <div className="flex flex-col items-center text-center">
                <div className="w-28 h-28 rounded-2xl mb-4 ring-4 ring-slate-100 bg-gradient-to-br from-electric-100 to-electric-50 flex items-center justify-center">
                  {driverLoading
                    ? <Skeleton className="w-28 h-28 rounded-2xl" />
                    : <span className="text-4xl font-bold text-electric-500" style={{ fontFamily: 'Space Grotesk' }}>
                        {driver?.full_name?.charAt(0) ?? '?'}
                      </span>
                  }
                </div>
                {driverLoading
                  ? <><Skeleton className="h-5 w-32 mb-1" /><Skeleton className="h-4 w-24" /></>
                  : <>
                      <h3 className="font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                        {driver?.full_name ?? '—'}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {driver?.vehicle ? `${driver.vehicle.plate} (${driver.vehicle.model})` : 'Inget fordon'}
                      </p>
                    </>
                }
              </div>

              <div className="space-y-3 text-sm">
                {driverLoading ? (
                  [...Array(3)].map((_, i) => <Skeleton key={i} className="h-4 w-full" />)
                ) : (
                  <>
                    {[
                      { icon: <Mail className="w-4 h-4" />, text: driver?.email ?? '—' },
                      { icon: <Phone className="w-4 h-4" />, text: driver?.phone ?? '—' },
                      { icon: <MapPin className="w-4 h-4" />, text: location ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` : 'Okänd position' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 text-slate-600">
                        <span className="text-slate-400 mt-0.5 shrink-0">{item.icon}</span>
                        <span>{item.text}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-1">
                      <Award className="w-4 h-4 text-amber-400" />
                      <span className="font-bold text-amber-500">{driver?.performance_score ?? 0}</span>
                      <div className="flex-1 progress-bar">
                        <div className="progress-bar-fill" style={{
                          width: `${driver?.performance_score ?? 0}%`,
                          background: `linear-gradient(90deg, ${getScoreColor(driver?.performance_score ?? 0)}, ${getScoreColor(driver?.performance_score ?? 0)}cc)`
                        }} />
                      </div>
                      <span className="text-xs text-slate-400">score</span>
                    </div>
                  </>
                )}
              </div>

              <div className="h-52 rounded-xl overflow-hidden border border-slate-100">
                <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  />
                  {location && (
                    <Marker position={[location.lat, location.lng]}>
                      <Popup>
                        <strong>{driver?.full_name ?? 'Förare'}</strong>
                        <p className="text-xs text-gray-500 mt-0.5">Nuvarande position</p>
                        {location.speed != null && <p className="text-xs text-gray-500">{location.speed.toFixed(0)} km/h</p>}
                      </Popup>
                    </Marker>
                  )}
                  {routePoints.length === 2 && (
                    <Polyline positions={routePoints} color="#F59E0B" weight={4} />
                  )}
                </MapContainer>
              </div>
              {!location && !driverLoading && (
                <p className="text-xs text-center text-slate-400">Ingen live-position ännu</p>
              )}
            </div>

            {/* ── Right: tab panels ── */}
            <div className="lg:col-span-2">

              {/* ══ TAB 1: Vilotidsrapport ══ */}
              {activeTab === 'vilotid' && (
                <div className="space-y-6">

                  {/* Hero KPI row */}
                  {sessionsLoading ? (
                    <div className="grid grid-cols-2 gap-3">
                      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {/* Körtid idag */}
                      <div className={`p-4 rounded-xl border ${todayMinutes > 780 ? 'border-red-200 bg-red-50' : todayMinutes > 360 ? 'border-amber-200 bg-amber-50' : 'border-slate-100 bg-white'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className={`w-4 h-4 ${todayMinutes > 780 ? 'text-red-500' : todayMinutes > 360 ? 'text-amber-500' : 'text-electric-500'}`} />
                          <span className="text-xs text-slate-500 font-medium">Körtid idag</span>
                        </div>
                        <div className={`text-2xl font-bold ${todayMinutes > 780 ? 'text-red-700' : todayMinutes > 360 ? 'text-amber-700' : 'text-slate-800'}`}
                          style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                          {formatDuration(todayMinutes)}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          Med kund {formatDuration(medKundMin)} · Ledig {formatDuration(ledigMin)} · max 13h
                        </div>
                      </div>

                      {/* Körpass idag */}
                      <div className="p-4 rounded-xl border border-slate-100 bg-white">
                        <div className="flex items-center gap-2 mb-2">
                          <Activity className="w-4 h-4 text-electric-500" />
                          <span className="text-xs text-slate-500 font-medium">Körpass idag</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                          {todaySessions.length}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {todaySessions.filter(s => s.status === 'active').length > 0 ? '🟢 Aktiv session' : 'Ingen aktiv session'}
                        </div>
                      </div>

                      {/* Vilotid idag — rast + logged-out windows, same math as mobile */}
                      <div className={`p-4 rounded-xl border ${todayBreakMinutes >= 30 || todayMinutes < 360 ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className={`w-4 h-4 ${todayBreakMinutes >= 30 || todayMinutes < 360 ? 'text-emerald-500' : 'text-red-500'}`} />
                          <span className="text-xs text-slate-500 font-medium">Vilotid idag</span>
                        </div>
                        <div className={`text-2xl font-bold ${todayBreakMinutes >= 30 || todayMinutes < 360 ? 'text-emerald-700' : 'text-red-700'}`}
                          style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                          {formatDuration(todayBreakMinutes + utloggadMin)}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          Rast {formatDuration(todayBreakMinutes)}
                          {utloggadCount > 0 ? ` · ${utloggadCount} utloggning ${formatDuration(utloggadMin)}` : ''}
                          {' · min 30 min efter 6h'}
                        </div>
                      </div>

                      {/* Compliance */}
                      <div className={`p-4 rounded-xl border ${
                        complianceStatus === 'ok' ? 'border-emerald-200 bg-emerald-50'
                        : complianceStatus === 'warning' ? 'border-amber-200 bg-amber-50'
                        : 'border-red-200 bg-red-50'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldCheck className={`w-4 h-4 ${
                            complianceStatus === 'ok' ? 'text-emerald-500'
                            : complianceStatus === 'warning' ? 'text-amber-500'
                            : 'text-red-500'
                          }`} />
                          <span className="text-xs text-slate-500 font-medium">Arbetstid · informativ</span>
                        </div>
                        <div className="mt-1">
                          <CompliancePill status={complianceStatus} large />
                        </div>
                        <div className="text-xs text-slate-400 mt-2">
                          Vägarbetstidslagen 2005:395 (arbetstid) — INTE det lagliga dygnsvila-beslutet.
                          Dygnsvila-status visas nedan (gemensam laglig motor).
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Authoritative dygnsvila verdict — the SINGLE legal engine
                      (compliance-verdict Edge Function), identical to mobile + gov. */}
                  {verdict && (
                    <div className={`p-4 rounded-xl border ${verdict.verdict === 'allowed' ? 'border-emerald-200 bg-emerald-50' : verdict.verdict === 'blocked' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {verdict.verdict === 'allowed'
                            ? <ShieldCheck className="w-4 h-4 text-emerald-500" />
                            : verdict.verdict === 'blocked'
                              ? <ShieldX className="w-4 h-4 text-red-500" />
                              : <ShieldAlert className="w-4 h-4 text-amber-500" />}
                          <span className="text-xs font-semibold text-slate-600">Dygnsvila-status · rullande 24 h (en gemensam laglig motor)</span>
                        </div>
                        <span className={`text-xs font-bold ${verdict.verdict === 'allowed' ? 'text-emerald-700' : verdict.verdict === 'blocked' ? 'text-red-700' : 'text-amber-700'}`}>
                          {verdict.verdict === 'allowed' ? 'FÅR KÖRA' : verdict.verdict === 'blocked' ? 'BLOCKERAD' : verdict.verdict === 'establishing' ? 'ÖVERVAKNING ETABLERAS' : 'OTILLRÄCKLIGT UNDERLAG'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><div className="text-slate-400 text-xs">Kör tidigast till</div><div className="font-semibold text-slate-800">{verdict.compliance_expires_at ? new Date(verdict.compliance_expires_at).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</div></div>
                        <div><div className="text-slate-400 text-xs">Tid kvar</div><div className="font-semibold text-slate-800">{formatDuration(verdict.drive_left_min)}</div></div>
                        <div><div className="text-slate-400 text-xs">Kvalificerad vila</div><div className="font-semibold text-slate-800">{verdict.beslutsunderlag.qualifying_rest ? `${formatDuration(verdict.beslutsunderlag.qualifying_rest.minutes)} (${verdict.match_type === 'split' ? 'delad' : 'sammanh.'})` : 'saknas'}</div></div>
                        <div><div className="text-slate-400 text-xs">Arbetstid 24 h</div><div className="font-semibold text-slate-800">{formatDuration(verdict.worked_min_rolling)}</div></div>
                      </div>
                      {verdict.beslutsunderlag.note ? (
                        <div className="mt-2 text-xs font-medium text-amber-700">{verdict.beslutsunderlag.note}</div>
                      ) : null}
                      <div className="mt-2 text-xs text-slate-500">
                        Beslutsunderlag: {verdict.beslutsunderlag.stint_events} stämplingar · {verdict.beslutsunderlag.trips_considered} resor{verdict.beslutsunderlag.excluded_work_from_trips ? ' (arbetstid borttagen)' : ''} · {verdict.beslutsunderlag.server_time_verified ? 'servertid verifierad' : 'klienttid'} · Förordning 1994:1297 3 §
                      </div>
                    </div>
                  )}

                  {/* Dygnsvila & regelhändelser — live from mobile enforcement */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-4 rounded-xl border border-slate-100 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-indigo-500" />
                        <span className="text-xs text-slate-500 font-medium">Senaste dygnsvila (11h-regeln)</span>
                      </div>
                      {restPeriods.length > 0 ? (
                        <>
                          <div className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                            {formatDuration(Math.round((new Date(restPeriods[0].ended_at).getTime() - new Date(restPeriods[0].started_at).getTime()) / 60000))}
                            {restPeriods[0].part === 2 ? ' · del 2' : ''}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {new Date(restPeriods[0].started_at).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            {' → '}
                            {new Date(restPeriods[0].ended_at).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            {` · ${restPeriods.length} registrerade (7 dgr)`}
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-slate-400">Ingen registrerad dygnsvila senaste 7 dagarna</div>
                      )}
                    </div>

                    <div className="p-4 rounded-xl border border-slate-100 bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`w-4 h-4 ${complianceEvents.length > 0 ? 'text-red-500' : 'text-emerald-500'}`} />
                        <span className="text-xs text-slate-500 font-medium">Regelhändelser (7 dgr)</span>
                      </div>
                      {complianceEvents.length > 0 ? (
                        <ul className="space-y-1">
                          {complianceEvents.slice(0, 4).map(ev => (
                            <li key={ev.id} className="text-xs text-slate-600 flex justify-between gap-2">
                              <span className="font-medium text-red-600">
                                {ev.event_type === 'dygnsvila_expired' ? 'Dygnsvila överskriden'
                                  : ev.event_type === 'vagarbetstid_daily_cap' ? 'Arbetstidsgräns nådd'
                                  : ev.event_type === 'dygnsvila_shortfall' ? 'Otillräcklig dygnsvila'
                                  : ev.event_type === 'break_warning' ? 'Rastvarning'
                                  : ev.event_type}
                              </span>
                              <span className="text-slate-400 shrink-0">
                                {new Date(ev.occurred_at).toLocaleString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-sm text-emerald-600">Inga regelbrott registrerade</div>
                      )}
                    </div>
                  </div>

                  {/* 7-day bar chart */}
                  <div className="card p-5">
                    <h3 className="text-sm font-semibold text-slate-700 mb-4">Körning senaste 7 dagarna (timmar)</h3>
                    {sessionsLoading ? (
                      <Skeleton className="h-40 w-full" />
                    ) : (
                      <div className="relative">
                        <Bar
                          data={{
                            labels: chartLabels,
                            datasets: [{
                              label: 'Körtid (h)',
                              data: chartData,
                              backgroundColor: 'rgba(6,182,212,0.85)',
                              borderRadius: 6,
                            }],
                          }}
                          options={{
                            responsive: true,
                            plugins: {
                              legend: { display: false },
                              tooltip: { callbacks: { label: ctx => `${ctx.parsed.y.toFixed(1)}h` } },
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                max: 14,
                                grid: { color: 'rgba(0,0,0,0.04)' },
                                border: { display: false },
                                ticks: { callback: v => `${v}h` },
                              },
                              x: { grid: { display: false }, border: { display: false } },
                            },
                          }}
                        />
                        {/* Legal max line at 13h */}
                        <div className="absolute right-0 text-[10px] text-red-400 font-semibold pointer-events-none"
                          style={{ top: `${((14 - 13) / 14) * 100}%`, transform: 'translateY(-50%)' }}>
                          Max 13h →
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Period summary table */}
                  <div className="rounded-xl border border-slate-100 overflow-hidden">
                    <div className="bg-slate-50 px-5 py-3 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-700 text-sm">Sammanfattning</h3>
                    </div>
                    <table className="min-w-full table-premium text-sm">
                      <thead>
                        <tr>
                          <th>Period</th>
                          <th>Körpass</th>
                          <th>Körtid</th>
                          <th>Vilotid</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="font-medium text-slate-700">Idag</td>
                          <td>{todaySessions.length}</td>
                          <td>{formatDuration(todayMinutes)}</td>
                          <td>{formatDuration(todayBreakMinutes + utloggadMin)}</td>
                          <td><CompliancePill status={complianceStatus} /></td>
                        </tr>
                        <tr>
                          <td className="font-medium text-slate-700">Denna vecka</td>
                          <td>{weekSessions.length}</td>
                          <td>{formatDuration(weekMinutes)}</td>
                          <td>{formatDuration(weekBreakMinutes + weekUtloggadMin)}</td>
                          <td><CompliancePill status={weekMinutes > 2880 ? 'violation' : weekMinutes > 2160 && weekMaxBreak < 30 ? 'warning' : 'ok'} /></td>
                        </tr>
                        <tr>
                          <td className="font-medium text-slate-700">Denna månad</td>
                          <td>{monthSessions.length}</td>
                          <td>{formatDuration(monthMinutes)}</td>
                          <td>{formatDuration(monthBreakMinutes + monthUtloggadMin)}</td>
                          <td><span className="text-xs text-slate-400">Informativt</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ══ TAB 2: Körpass ══ */}
              {activeTab === 'korpass' && (
                <div className="space-y-4">
                  {sessionsLoading ? (
                    <div className="space-y-2">
                      {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                    </div>
                  ) : visibleSessions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Clock className="w-10 h-10 text-slate-200 mb-3" />
                      <p className="text-sm font-medium text-slate-500">Inga körpass i vald period</p>
                      <p className="text-xs text-slate-400 mt-1">Körpass skapas automatiskt via mobilappen</p>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-slate-100 overflow-hidden">
                      <table className="min-w-full table-premium text-sm">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Datum</th>
                            <th>Start</th>
                            <th>Slut</th>
                            <th>Körtid</th>
                            <th>Raster</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleSessions.map((session) => {
                            const sessionStatus = getSessionStatus(session);
                            const breaks = session.breaks ?? [];
                            const isExpanded = expandedSession === session.id;
                            const isActive = session.status === 'active';
                            const dayKey = localDayKey(new Date(session.started_at));
                            const dayStints = stintPairsForDay(stints, dayKey);
                            const dayLogouts = logoutGapsForDay(stints, dayKey);

                            return (
                              <React.Fragment key={session.id}>
                                <tr
                                  className={`cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}
                                  onClick={() => setExpandedSession(isExpanded ? null : session.id)}
                                >
                                  <td className="w-8">
                                    {isExpanded
                                      ? <ChevronUp className="w-4 h-4 text-slate-400" />
                                      : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                  </td>
                                  <td className="text-xs text-slate-500 whitespace-nowrap">
                                    {new Date(session.started_at).toLocaleDateString('sv-SE')}
                                  </td>
                                  <td className="text-xs whitespace-nowrap">
                                    {new Date(session.started_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                  </td>
                                  <td className="text-xs whitespace-nowrap text-slate-500">
                                    {session.ended_at
                                      ? new Date(session.ended_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
                                      : '—'}
                                  </td>
                                  <td className="font-medium text-slate-700">
                                    {formatDuration(session.driving_minutes ?? 0)}
                                  </td>
                                  <td className="text-xs text-slate-500">
                                    {breaks.length > 0
                                      ? `${breaks.length} rast${breaks.length > 1 ? 'er' : ''}`
                                      : '—'}
                                  </td>
                                  <td>
                                    {isActive ? (
                                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                        <span className="live-dot" style={{ width: 6, height: 6 }} />
                                        Aktiv
                                      </span>
                                    ) : (
                                      <CompliancePill status={sessionStatus} />
                                    )}
                                  </td>
                                </tr>

                                {/* Expanded detail — stints, raster, utloggning (same rows as mobile) */}
                                {isExpanded && (
                                  <tr>
                                    <td colSpan={7} className="bg-slate-50/80 px-6 py-4">
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                          <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">In-/utloggning</div>
                                          {dayStints.length > 0 ? (
                                            <table className="min-w-full text-xs">
                                              <thead>
                                                <tr className="text-slate-400">
                                                  <th className="text-left pb-1 font-medium">Inloggad</th>
                                                  <th className="text-left pb-1 font-medium">Utloggad</th>
                                                  <th className="text-left pb-1 font-medium">Längd</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {dayStints.map((p) => (
                                                  <tr key={p.start} className="border-t border-slate-100">
                                                    <td className="py-1.5 text-slate-600 font-medium">
                                                      {new Date(p.start).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                    <td className="py-1.5 text-slate-500">
                                                      {p.end
                                                        ? new Date(p.end).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
                                                        : 'Pågår…'}
                                                    </td>
                                                    <td className="py-1.5 font-medium text-slate-700">{formatDuration(p.minutes)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <p className="text-xs text-slate-400">Inga stämplingar denna dag</p>
                                          )}
                                        </div>

                                        <div>
                                          <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Raster</div>
                                          {breaks.length > 0 ? (
                                            <table className="min-w-full text-xs">
                                              <thead>
                                                <tr className="text-slate-400">
                                                  <th className="text-left pb-1 font-medium">Typ</th>
                                                  <th className="text-left pb-1 font-medium">Start</th>
                                                  <th className="text-left pb-1 font-medium">Slut</th>
                                                  <th className="text-left pb-1 font-medium">Längd</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {breaks.map((b) => (
                                                  <tr key={b.id} className="border-t border-slate-100">
                                                    <td className="py-1.5 capitalize font-medium text-slate-600">
                                                      {b.type === 'lunch' ? 'Lunch' : 'Paus'}
                                                    </td>
                                                    <td className="py-1.5 text-slate-500">
                                                      {new Date(b.started_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                    <td className="py-1.5 text-slate-500">
                                                      {b.ended_at
                                                        ? new Date(b.ended_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })
                                                        : 'Pågår…'}
                                                    </td>
                                                    <td className="py-1.5 font-medium text-slate-700">
                                                      {formatDuration(breakDurationMin(b))}
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <p className="text-xs text-slate-400">Inga raster denna dag</p>
                                          )}
                                        </div>

                                        <div>
                                          <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Utloggad (off duty)</div>
                                          {dayLogouts.length > 0 ? (
                                            <table className="min-w-full text-xs">
                                              <thead>
                                                <tr className="text-slate-400">
                                                  <th className="text-left pb-1 font-medium">Start</th>
                                                  <th className="text-left pb-1 font-medium">Slut</th>
                                                  <th className="text-left pb-1 font-medium">Längd</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {dayLogouts.map((g) => (
                                                  <tr key={g.start} className="border-t border-slate-100">
                                                    <td className="py-1.5 text-slate-600 font-medium">
                                                      {new Date(g.start).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                    <td className="py-1.5 text-slate-500">
                                                      {new Date(g.end).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                    <td className="py-1.5 font-medium text-slate-700">{formatDuration(g.minutes)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <p className="text-xs text-slate-400">Inga utloggningar denna dag</p>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ══ TAB 3: Fordon (Car Performance — unchanged) ══ */}
              {activeTab === 'car' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {driverLoading ? (
                    [...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
                  ) : (
                    <>
                      <MetricCard icon={<Car className="w-4 h-4" />} label="Fordonspoäng">
                        <div className="flex items-center gap-2">
                          <div className="progress-bar" style={{ width: 60 }}>
                            <div className="progress-bar-fill" style={{ width: `${driver?.performance_score ?? 0}%` }} />
                          </div>
                          <span>{driver?.performance_score ?? 0}%</span>
                        </div>
                      </MetricCard>
                      <MetricCard icon={<Car className="w-4 h-4" />} label="Registreringsnummer">
                        {driver?.vehicle?.plate ?? '—'}
                      </MetricCard>
                      <MetricCard icon={<Car className="w-4 h-4" />} label="Fordonsmodell">
                        {driver?.vehicle?.model ?? '—'}
                      </MetricCard>
                      <MetricCard icon={<Car className="w-4 h-4" />} label="Årsmodell">
                        {driver?.vehicle?.year ?? '—'}
                      </MetricCard>
                      <MetricCard icon={<CheckCircle className="w-4 h-4" />} label="Fordonsstatus">
                        <span className={`capitalize ${driver?.vehicle?.status === 'active' ? 'text-emerald-600' : 'text-amber-500'}`}>
                          {driver?.vehicle?.status ?? 'Inget fordon'}
                        </span>
                      </MetricCard>
                      <MetricCard icon={<Navigation className="w-4 h-4" />} label="Senaste hastighet">
                        {location?.speed != null ? `${location.speed.toFixed(0)} km/h` : '—'}
                      </MetricCard>
                      <MetricCard icon={<Gauge className="w-4 h-4" />} label="Bränsleförbrukning">
                        <span className="text-slate-400 text-xs">Ingen OBD-enhet</span>
                      </MetricCard>
                      <MetricCard icon={<AlertTriangle className="w-4 h-4" />} label="Hastighetsöverträdelser">
                        <span className="text-slate-400 text-xs">Ingen OBD-enhet</span>
                      </MetricCard>
                    </>
                  )}
                </div>
              )}

              {/* ══ TAB 4: Inkomst — AML Ledger ══ */}
              {activeTab === 'income' && (() => {
                const incomeRows  = transactions.filter(tx => tx.type === 'income');
                const expenseRows = transactions.filter(tx => tx.type === 'expense');
                const verified    = incomeRows.filter(tx => tx.trip_id != null);
                const unverified  = incomeRows.filter(tx => tx.trip_id == null);
                const verifiedSum   = verified.reduce((s, tx) => s + tx.amount, 0);
                const unverifiedSum = unverified.reduce((s, tx) => s + tx.amount, 0);
                const expenseSum    = expenseRows.reduce((s, tx) => s + tx.amount, 0);

                const PayBadge = ({ method }: { method: string | null | undefined }) => {
                  if (!method) return <span className="text-xs text-slate-400">—</span>;
                  const cfg: Record<string, { label: string; cls: string; Icon: React.ComponentType<{ className?: string }> }> = {
                    card:    { label: 'Kort',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CreditCard },
                    swish:   { label: 'Swish',   cls: 'bg-blue-50 text-blue-700 border-blue-200',         Icon: Smartphone },
                    cash:    { label: 'Kontant', cls: 'bg-amber-50 text-amber-700 border-amber-200',       Icon: Banknote },
                    invoice: { label: 'Faktura', cls: 'bg-slate-50 text-slate-600 border-slate-200',       Icon: FileText },
                  };
                  const c = cfg[method] ?? { label: method, cls: 'bg-slate-50 text-slate-600 border-slate-200', Icon: DollarSign };
                  return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${c.cls}`}>
                      <c.Icon className="w-3 h-3" />{c.label}
                    </span>
                  );
                };

                return (
                  <div className="space-y-5">
                    {/* Income per app — mirrors mobile Ekonomi */}
                    {!incomeByAppLoading && incomeByApp.length > 0 && (
                      <div className="rounded-xl border border-slate-100 overflow-hidden">
                        <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                          <h3 className="font-semibold text-slate-700 text-sm">Inkomst per app</h3>
                        </div>
                        <div className="divide-y divide-slate-50">
                          {incomeByApp.map(row => (
                            <div key={row.app} className="flex items-center justify-between px-4 py-3 text-sm">
                              <div>
                                <span className="font-semibold text-slate-800">{row.app}</span>
                                <span className="text-slate-400 ml-2">{row.trips} resor</span>
                              </div>
                              <span className="font-bold text-emerald-600">{row.total.toFixed(0)} kr</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Taxi apps — mirrors mobile Mina appar */}
                    {!appsLoading && driverApps.length > 0 && (
                      <div className="rounded-xl border border-slate-100 overflow-hidden">
                        <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                          <h3 className="font-semibold text-slate-700 text-sm">Anslutna taxiappar</h3>
                        </div>
                        <div className="flex flex-wrap gap-2 p-4">
                          {driverApps.map(app => (
                            <span
                              key={app.id}
                              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                                app.status === 'connected'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-slate-50 text-slate-600 border-slate-200'
                              }`}
                            >
                              {app.app_name} · {app.status}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AML Summary bar */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-4 rounded-xl border border-emerald-100 bg-emerald-50 flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-emerald-700">
                          <Lock className="w-4 h-4" />
                          <span className="text-xs font-semibold uppercase tracking-wide">Verifierade</span>
                        </div>
                        <span className="text-xl font-bold text-emerald-700">{verifiedSum.toFixed(0)} kr</span>
                        <span className="text-xs text-emerald-600">{verified.length} transaktioner med kopplad resa</span>
                      </div>
                      <div className="p-4 rounded-xl border border-amber-100 bg-amber-50 flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-amber-700">
                          <Unlock className="w-4 h-4" />
                          <span className="text-xs font-semibold uppercase tracking-wide">Ej verifierade</span>
                        </div>
                        <span className="text-xl font-bold text-amber-700">{unverifiedSum.toFixed(0)} kr</span>
                        <span className="text-xs text-amber-600">{unverified.length} utan kopplad resa</span>
                      </div>
                      <div className="p-4 rounded-xl border border-slate-100 bg-slate-50 flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-slate-600">
                          <DollarSign className="w-4 h-4" />
                          <span className="text-xs font-semibold uppercase tracking-wide">Utgifter</span>
                        </div>
                        <span className="text-xl font-bold text-slate-700">{expenseSum.toFixed(0)} kr</span>
                        <span className="text-xs text-slate-500">{expenseRows.length} poster</span>
                      </div>
                    </div>

                    {/* Ledger table */}
                    <div className="rounded-xl border border-slate-100 overflow-hidden">
                      <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                        <h3 className="font-semibold text-slate-700 text-sm">Transaktionslogg — DAC7 / Skatteverket</h3>
                        <span className="text-xs text-slate-400">EU Direktiv 2021/514 · Prop. 2025/26:43</span>
                      </div>
                      {txLoading ? (
                        <div className="p-4 space-y-2">
                          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                        </div>
                      ) : transactions.length === 0 ? (
                        <div className="py-10 text-center text-sm text-slate-400">Inga transaktioner registrerade</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-slate-100 bg-slate-50/70">
                                <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Datum</th>
                                <th className="text-left px-3 py-2.5 text-slate-500 font-medium">Plattform</th>
                                <th className="text-left px-3 py-2.5 text-slate-500 font-medium">Rutt</th>
                                <th className="text-left px-3 py-2.5 text-slate-500 font-medium">Km</th>
                                <th className="text-left px-3 py-2.5 text-slate-500 font-medium">Betalning</th>
                                <th className="text-right px-4 py-2.5 text-slate-500 font-medium">Belopp</th>
                                <th className="text-left px-3 py-2.5 text-slate-500 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                              {transactions.map(tx => {
                                const isCashUnverified = tx.trip?.payment_method === 'cash' && !tx.trip_id;
                                const isIncome = tx.type === 'income';
                                return (
                                  <tr
                                    key={tx.id}
                                    className={`hover:bg-slate-50 transition ${isCashUnverified ? 'border-l-2 border-l-amber-400' : ''}`}
                                  >
                                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                      {new Date(tx.occurred_at).toLocaleDateString('sv-SE')}
                                    </td>
                                    <td className="px-3 py-3 text-slate-500">
                                      {tx.trip?.platform ?? tx.category ?? '—'}
                                    </td>
                                    <td className="px-3 py-3 max-w-[180px]">
                                      {tx.trip ? (
                                        <div className="flex items-center gap-1">
                                          <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                                          <span className="truncate text-slate-600">
                                            {tx.trip.origin_address ?? '—'} → {tx.trip.dest_address ?? '—'}
                                          </span>
                                        </div>
                                      ) : (
                                        <span className="text-slate-400">{tx.note ?? '—'}</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-3 text-slate-500">
                                      {tx.trip?.distance_km != null ? `${tx.trip.distance_km.toFixed(1)} km` : '—'}
                                    </td>
                                    <td className="px-3 py-3">
                                      <PayBadge method={tx.trip?.payment_method} />
                                    </td>
                                    <td className="px-4 py-3 text-right font-bold whitespace-nowrap">
                                      <span className={isIncome ? 'text-emerald-600' : 'text-red-500'}>
                                        {isIncome ? '+' : '−'}{tx.amount.toFixed(0)} kr
                                      </span>
                                    </td>
                                    <td className="px-3 py-3">
                                      {isIncome && tx.trip_id ? (
                                        <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-0.5 font-medium">
                                          <Lock className="w-3 h-3" />Verifierad resa
                                        </span>
                                      ) : isIncome ? (
                                        <span
                                          className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-0.5 font-medium"
                                          title={isCashUnverified ? 'Kontantinkomst utan kopplad resa — rapporteras till Skatteverket per DAC7' : 'Ingen kopplad resa hittad'}
                                        >
                                          <AlertTriangle className="w-3 h-3" />Ej verifierbar
                                        </span>
                                      ) : (
                                        <span className="text-slate-400">Utgift</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TachographDetail;
